import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Briefcase, Users, UserCheck, Calendar, MapPin, Star, ArrowRight } from "lucide-react";

function money(n) { return "₹ " + Number(n || 0).toLocaleString("en-IN"); }
function fmt(d) { try { return new Date(d).toLocaleDateString("en-IN", { day:"2-digit", month:"short", year:"numeric" }); } catch { return d; } }

const STAGES = ["Applied","Screening","Interview","Offered","Hired","Rejected"];
const STAGE_STYLE = {
  Applied: "bg-slate-100 text-slate-700",
  Screening: "bg-red-50 text-red-700",
  Interview: "bg-orange-50 text-orange-700",
  Offered: "bg-purple-50 text-purple-700",
  Hired: "bg-emerald-50 text-emerald-700",
  Rejected: "bg-red-50 text-red-700",
};

export default function Recruitment() {
  const { user } = useAuth();
  const [tab, setTab] = useState("jobs");
  const [jobs, setJobs] = useState([]);
  const [cands, setCands] = useState([]);
  const [summary, setSummary] = useState(null);
  const [showJob, setShowJob] = useState(false);
  const [showCand, setShowCand] = useState(false);
  const [openCand, setOpenCand] = useState(null);
  const [scheduleFor, setScheduleFor] = useState(null);

  const [jobForm, setJobForm] = useState({ title:"", department:"Engineering", location:"Bengaluru HQ", employment_type:"Full-time", experience:"3-5 years", salary_range:"", description:"", requirements:"", positions_open:1 });
  const [candForm, setCandForm] = useState({ name:"", email:"", phone:"", current_ctc:0, expected_ctc:0, notice_period:"30 days", experience_years:0, skills:"", job_id:"", source:"LinkedIn", notes:"" });
  const [intForm, setIntForm] = useState({ round_name:"Tech Screen", scheduled_at:"", interviewer:"", interviewer_email:"", mode:"Google Meet", meeting_link:"" });

  const load = async () => {
    const [j, c, s] = await Promise.all([
      api.get("/jobs"),
      api.get("/candidates"),
      api.get("/recruitment/summary"),
    ]);
    setJobs(j.data); setCands(c.data); setSummary(s.data);
    if (!candForm.job_id && j.data[0]) setCandForm(f => ({...f, job_id: j.data[0].id}));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const candsByStage = useMemo(() => {
    const map = Object.fromEntries(STAGES.map(s => [s, []]));
    for (const c of cands) if (map[c.status]) map[c.status].push(c);
    return map;
  }, [cands]);

  const applicantsByJob = useMemo(() => {
    const m = {};
    for (const c of cands) m[c.job_id] = (m[c.job_id] || 0) + 1;
    return m;
  }, [cands]);

  const createJob = async (e) => {
    e.preventDefault();
    try {
      await api.post("/jobs", { ...jobForm, positions_open: Number(jobForm.positions_open), requirements: jobForm.requirements.split(",").map(s=>s.trim()).filter(Boolean) });
      toast.success(`Job "${jobForm.title}" posted`);
      setShowJob(false);
      setJobForm({ title:"", department:"Engineering", location:"Bengaluru HQ", employment_type:"Full-time", experience:"3-5 years", salary_range:"", description:"", requirements:"", positions_open:1 });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const createCand = async (e) => {
    e.preventDefault();
    try {
      await api.post("/candidates", { ...candForm, current_ctc: Number(candForm.current_ctc), expected_ctc: Number(candForm.expected_ctc), experience_years: Number(candForm.experience_years), skills: candForm.skills.split(",").map(s=>s.trim()).filter(Boolean) });
      toast.success(`${candForm.name} added`);
      setShowCand(false);
      setCandForm({ ...candForm, name:"", email:"", phone:"", current_ctc:0, expected_ctc:0, notice_period:"30 days", experience_years:0, skills:"", notes:"" });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const moveStage = async (id, status) => {
    try { await api.put(`/candidates/${id}/status`, { status }); toast.success(`Moved to ${status}`); load(); if (openCand) setOpenCand({ ...openCand, status }); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const scheduleInterview = async (e) => {
    e.preventDefault();
    try {
      const upd = await api.post(`/candidates/${scheduleFor.id}/interview`, intForm);
      const iv = (upd.data.interviews || []).slice(-1)[0];
      const parts = [];
      if (iv?.candidate_email_sent) parts.push(`candidate emailed`);
      if (iv?.panel_email_sent) parts.push(`panel emailed`);
      toast.success(`Interview scheduled${parts.length ? ` · ${parts.join(", ")}` : ""}`);
      setScheduleFor(null);
      setIntForm({ round_name:"Tech Screen", scheduled_at:"", interviewer:"", interviewer_email:"", mode:"Google Meet", meeting_link:"" });
      load();
      setOpenCand(upd.data);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="space-y-6" data-testid="recruit-root">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase font-semibold tracking-[0.1em] text-slate-500">Recruitment</div>
          <h1 className="mt-1 font-display text-3xl font-extrabold tracking-tight text-slate-900">Hire the next Techtreno-nian.</h1>
          <p className="mt-1 text-sm text-slate-500">Open roles, candidate pipeline and interview scheduling — one place.</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setShowJob(true)} variant="outline" data-testid="new-job-btn"><Plus className="h-4 w-4 mr-1.5" strokeWidth={1.8} /> Post job</Button>
          <Button onClick={() => setShowCand(true)} className="bg-[#DC2626] hover:bg-[#B91C1C]" data-testid="new-cand-btn"><Plus className="h-4 w-4 mr-1.5" strokeWidth={1.8} /> Add candidate</Button>
        </div>
      </div>

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatBox label="Open positions" value={summary.positions_open} sub={`${summary.open_jobs} jobs`} icon={Briefcase} />
          <StatBox label="Candidates" value={summary.total_candidates} icon={Users} />
          <StatBox label="In interview" value={summary.by_stage?.Interview || 0} icon={Calendar} />
          <StatBox label="Hired" value={summary.by_stage?.Hired || 0} icon={UserCheck} tone="green" />
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="jobs" data-testid="tab-jobs">Job openings</TabsTrigger>
          <TabsTrigger value="pipeline" data-testid="tab-pipeline">Candidate pipeline</TabsTrigger>
        </TabsList>
        <TabsContent value="jobs">
          <div className="grid md:grid-cols-2 gap-4">
            {jobs.map(j => (
              <Card key={j.id} className="p-5 rounded-xl border-slate-200/60" data-testid={`job-${j.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-semibold text-slate-900">{j.title}</div>
                      <Badge className={j.status === "Open" ? "bg-emerald-50 text-emerald-700 border-0" : "bg-slate-100 text-slate-700 border-0"}>{j.status}</Badge>
                    </div>
                    <div className="mt-1 text-xs text-slate-500 flex flex-wrap items-center gap-x-3">
                      <span className="flex items-center gap-1"><Briefcase className="h-3 w-3" strokeWidth={1.5} /> {j.department}</span>
                      <span className="flex items-center gap-1"><MapPin className="h-3 w-3" strokeWidth={1.5} /> {j.location}</span>
                      <span>{j.experience}</span>
                      <span>{j.salary_range}</span>
                    </div>
                    <div className="text-sm text-slate-600 mt-2">{j.description}</div>
                    <div className="mt-2 flex flex-wrap gap-1">
                      {j.requirements.slice(0,4).map((r) => (
                        <span key={`${j.id}-req-${r}`} className="text-[11px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{r}</span>
                      ))}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-[10px] uppercase font-semibold tracking-wider text-slate-500">Positions</div>
                    <div className="font-display text-2xl font-bold text-[#DC2626]">{j.positions_open}</div>
                    <div className="text-xs text-slate-500 mt-1">{applicantsByJob[j.id] || 0} applicants</div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </TabsContent>
        <TabsContent value="pipeline">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
            {STAGES.map(s => (
              <div key={s} className="min-w-0">
                <div className="flex items-center justify-between mb-2">
                  <span className={`text-[11px] uppercase font-semibold tracking-wider px-2 py-1 rounded-md ${STAGE_STYLE[s]}`}>{s}</span>
                  <span className="text-xs text-slate-500">{candsByStage[s].length}</span>
                </div>
                <div className="space-y-2">
                  {candsByStage[s].map(c => (
                    <Card key={c.id} onClick={() => setOpenCand(c)} className="p-3 rounded-lg border-slate-200/60 cursor-pointer hover:shadow-md hover:-translate-y-0.5 transition-all" data-testid={`cand-${c.id}`}>
                      <div className="text-sm font-semibold text-slate-900">{c.name}</div>
                      <div className="text-[11px] text-slate-500 truncate">{c.job_title}</div>
                      <div className="flex items-center justify-between mt-2">
                        <div className="text-[11px] text-slate-500">{c.experience_years}y</div>
                        {c.rating > 0 && (
                          <span className="text-[11px] text-orange-500 flex items-center gap-0.5"><Star className="h-3 w-3" strokeWidth={2} />{c.rating}</span>
                        )}
                      </div>
                    </Card>
                  ))}
                  {candsByStage[s].length === 0 && <div className="text-[11px] text-slate-400 text-center py-4 border border-dashed rounded-md">Empty</div>}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* New job dialog */}
      <Dialog open={showJob} onOpenChange={setShowJob}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Post a new job</DialogTitle></DialogHeader>
          <form onSubmit={createJob} className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <FormField label="Title *"><Input required value={jobForm.title} onChange={e => setJobForm({...jobForm, title: e.target.value})} data-testid="job-title" /></FormField>
            <FormField label="Department"><Input value={jobForm.department} onChange={e => setJobForm({...jobForm, department: e.target.value})} /></FormField>
            <FormField label="Location"><Input value={jobForm.location} onChange={e => setJobForm({...jobForm, location: e.target.value})} /></FormField>
            <FormField label="Experience"><Input value={jobForm.experience} onChange={e => setJobForm({...jobForm, experience: e.target.value})} /></FormField>
            <FormField label="Salary range"><Input value={jobForm.salary_range} onChange={e => setJobForm({...jobForm, salary_range: e.target.value})} placeholder="₹8-12 LPA" /></FormField>
            <FormField label="Positions open"><Input type="number" min="1" value={jobForm.positions_open} onChange={e => setJobForm({...jobForm, positions_open: e.target.value})} /></FormField>
            <div className="md:col-span-2"><FormField label="Description"><Textarea rows={3} value={jobForm.description} onChange={e => setJobForm({...jobForm, description: e.target.value})} data-testid="job-desc" /></FormField></div>
            <div className="md:col-span-2"><FormField label="Requirements (comma separated)"><Textarea rows={2} value={jobForm.requirements} onChange={e => setJobForm({...jobForm, requirements: e.target.value})} placeholder="VMware VCP-DCV, vSAN, Python" /></FormField></div>
            <div className="md:col-span-2 flex justify-end"><Button type="submit" className="bg-[#DC2626] hover:bg-[#B91C1C]" data-testid="job-submit">Post job</Button></div>
          </form>
        </DialogContent>
      </Dialog>

      {/* New candidate dialog */}
      <Dialog open={showCand} onOpenChange={setShowCand}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Add candidate</DialogTitle></DialogHeader>
          <form onSubmit={createCand} className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <FormField label="Name *"><Input required value={candForm.name} onChange={e => setCandForm({...candForm, name: e.target.value})} data-testid="cand-name" /></FormField>
            <FormField label="Email *"><Input type="email" required value={candForm.email} onChange={e => setCandForm({...candForm, email: e.target.value})} data-testid="cand-email" /></FormField>
            <FormField label="Phone"><Input value={candForm.phone} onChange={e => setCandForm({...candForm, phone: e.target.value})} /></FormField>
            <FormField label="Job">
              <Select value={candForm.job_id} onValueChange={v => setCandForm({...candForm, job_id: v})}>
                <SelectTrigger data-testid="cand-job"><SelectValue placeholder="Pick a job" /></SelectTrigger>
                <SelectContent>{jobs.map(j => <SelectItem key={j.id} value={j.id}>{j.title}</SelectItem>)}</SelectContent>
              </Select>
            </FormField>
            <FormField label="Current CTC (₹)"><Input type="number" value={candForm.current_ctc} onChange={e => setCandForm({...candForm, current_ctc: e.target.value})} /></FormField>
            <FormField label="Expected CTC (₹)"><Input type="number" value={candForm.expected_ctc} onChange={e => setCandForm({...candForm, expected_ctc: e.target.value})} /></FormField>
            <FormField label="Notice period"><Input value={candForm.notice_period} onChange={e => setCandForm({...candForm, notice_period: e.target.value})} /></FormField>
            <FormField label="Experience (yrs)"><Input type="number" step="0.5" value={candForm.experience_years} onChange={e => setCandForm({...candForm, experience_years: e.target.value})} /></FormField>
            <div className="md:col-span-2"><FormField label="Skills (comma separated)"><Input value={candForm.skills} onChange={e => setCandForm({...candForm, skills: e.target.value})} /></FormField></div>
            <FormField label="Source">
              <Select value={candForm.source} onValueChange={v => setCandForm({...candForm, source: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{["LinkedIn","Naukri","Referral","Career Page","InstaHyre","Other"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </FormField>
            <div className="md:col-span-2 flex justify-end"><Button type="submit" className="bg-[#DC2626] hover:bg-[#B91C1C]" data-testid="cand-submit">Add candidate</Button></div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Candidate detail */}
      <Dialog open={!!openCand} onOpenChange={(o) => !o && setOpenCand(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{openCand?.name}</DialogTitle></DialogHeader>
          {openCand && (
            <div className="space-y-4">
              <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-slate-600">
                <span>{openCand.email}</span>
                <span>{openCand.phone}</span>
                <span>{openCand.experience_years}y experience</span>
                <span>Notice: {openCand.notice_period}</span>
              </div>
              <div className="flex flex-wrap gap-x-6 text-sm">
                <div>Applied for: <span className="font-semibold text-slate-900">{openCand.job_title}</span></div>
                <div>Source: <span className="font-semibold text-slate-900">{openCand.source}</span></div>
                <div>Current: <span className="font-semibold text-slate-900">{money(openCand.current_ctc)}</span></div>
                <div>Expected: <span className="font-semibold text-slate-900">{money(openCand.expected_ctc)}</span></div>
              </div>
              <div className="flex flex-wrap gap-1">
                {openCand.skills?.map((s) => <span key={s} className="text-[11px] px-2 py-0.5 rounded bg-red-50 text-[#DC2626]">{s}</span>)}
              </div>

              <div>
                <div className="text-xs uppercase font-semibold tracking-wider text-slate-500 mb-2">Move stage</div>
                <div className="flex flex-wrap gap-2">
                  {STAGES.map(s => (
                    <Button key={s} size="sm" variant={openCand.status === s ? "default" : "outline"}
                            className={openCand.status === s ? "bg-[#DC2626] hover:bg-[#B91C1C]" : ""}
                            onClick={() => moveStage(openCand.id, s)} data-testid={`move-${s}`}>
                      {s}
                    </Button>
                  ))}
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs uppercase font-semibold tracking-wider text-slate-500">Interviews</div>
                  <Button size="sm" variant="outline" onClick={() => setScheduleFor(openCand)} data-testid="schedule-interview-btn">
                    <Plus className="h-3.5 w-3.5 mr-1" strokeWidth={1.8} /> Schedule
                  </Button>
                </div>
                {openCand.interviews?.length ? (
                  <div className="space-y-2">
                    {openCand.interviews.map((iv, i) => (
                      <Card key={iv.id || i} className="p-3 rounded-lg border-slate-200/60">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="text-sm font-semibold text-slate-900">{iv.round_name}</div>
                            <div className="text-xs text-slate-500">
                              {iv.interviewer} · {iv.mode} · {fmt(iv.scheduled_at)}
                            </div>
                          </div>
                          <Badge className={iv.status === "Completed" ? "bg-emerald-50 text-emerald-700 border-0" : "bg-orange-50 text-orange-700 border-0"}>{iv.status}</Badge>
                        </div>
                        {iv.feedback && <div className="text-xs text-slate-600 mt-2">💬 {iv.feedback} · Rating {iv.rating} · <b>{iv.outcome}</b></div>}
                        <div className="flex gap-2 mt-2 text-[10px]">
                          {iv.candidate_email_sent ? <span className="px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 font-semibold">✓ Candidate emailed</span> : <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">Candidate not emailed</span>}
                          {iv.panel_email_sent ? <span className="px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 font-semibold">✓ Panel emailed</span> : (iv.interviewer_email ? <span className="px-1.5 py-0.5 rounded bg-orange-50 text-orange-700">Panel send failed</span> : null)}
                        </div>
                      </Card>
                    ))}
                  </div>
                ) : <div className="text-xs text-slate-500">No interviews scheduled.</div>}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Schedule interview */}
      <Dialog open={!!scheduleFor} onOpenChange={(o) => !o && setScheduleFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Schedule interview · {scheduleFor?.name}</DialogTitle></DialogHeader>
          <form onSubmit={scheduleInterview} className="space-y-3">
            <FormField label="Round name"><Input value={intForm.round_name} onChange={e => setIntForm({...intForm, round_name: e.target.value})} data-testid="int-round" /></FormField>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <FormField label="Interviewer name"><Input value={intForm.interviewer} onChange={e => setIntForm({...intForm, interviewer: e.target.value})} placeholder="Rohit Sharma" data-testid="int-interviewer" required /></FormField>
              <FormField label="Interviewer email (for calendar invite)"><Input type="email" value={intForm.interviewer_email} onChange={e => setIntForm({...intForm, interviewer_email: e.target.value})} placeholder="rohit@techtreno.com" data-testid="int-interviewer-email" /></FormField>
            </div>
            <FormField label="Scheduled at"><Input type="datetime-local" value={intForm.scheduled_at} onChange={e => setIntForm({...intForm, scheduled_at: e.target.value})} data-testid="int-time" required /></FormField>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <FormField label="Mode">
                <Select value={intForm.mode} onValueChange={v => setIntForm({...intForm, mode: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{["Google Meet","MS Teams","Zoom","In person","Phone"].map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </FormField>
              <FormField label="Meeting link (optional)"><Input value={intForm.meeting_link} onChange={e => setIntForm({...intForm, meeting_link: e.target.value})} placeholder="https://meet.google.com/..." data-testid="int-link" /></FormField>
            </div>
            <div className="rounded-lg border border-red-100 bg-red-50/50 px-3 py-2 text-xs text-slate-600">
              <span className="font-semibold text-[#DC2626]">Auto-email:</span> candidate ({scheduleFor?.email}) will receive an interview invite. {intForm.interviewer_email && <>Interviewer <span className="font-semibold">{intForm.interviewer_email}</span> will get the panel brief.</>}
            </div>
            <div className="flex justify-end"><Button type="submit" className="bg-[#DC2626] hover:bg-[#B91C1C]" data-testid="int-submit">Schedule & email</Button></div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatBox({ label, value, sub, icon: Icon, tone="red" }) {
  const tones = { red: "text-[#DC2626] bg-red-50", green: "text-emerald-600 bg-emerald-50" };
  return (
    <Card className="p-5 rounded-xl border-slate-200/60">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] uppercase font-semibold tracking-wider text-slate-500">{label}</div>
          <div className="mt-1 font-display text-2xl font-bold text-slate-900">{value}</div>
          {sub && <div className="text-xs text-slate-500 mt-0.5">{sub}</div>}
        </div>
        <div className={`h-10 w-10 rounded-lg grid place-items-center ${tones[tone]}`}><Icon className="h-5 w-5" strokeWidth={1.5} /></div>
      </div>
    </Card>
  );
}

function FormField({ label, children }) {
  return <div className="space-y-1.5"><Label>{label}</Label>{children}</div>;
}
